import { NgTemplateOutlet } from '@angular/common';
import {
  DestroyRef,
  inject,
  SimpleChanges,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  output,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { animationFrameScheduler, fromEvent } from 'rxjs';
import { distinctUntilChanged, take, map, observeOn, share, startWith } from 'rxjs/operators';

import type {
  ReportDashboardComponentViewData,
  SummaryData,
  SummaryDataItem,
  VisualizationData,
} from '@core/reports-types';
import {
  DividerComponent,
  ResizeObserverEntryDirective,
  RtTooltip,
} from '@core/user-interface-legacy';
import { generateUuid } from '@core/utils-legacy';

@Component({
  selector: 'reports-dashboards-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, RtTooltip, DividerComponent],
  host: { '[class.animated]': 'animated()' },
})
export class DashboardsSummaryComponent {
  private readonly elRef = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly resizeObserverEntry = inject(ResizeObserverEntryDirective);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly cdRef = inject(ChangeDetectorRef);

  public readonly vcRef = inject(ViewContainerRef);
  public readonly maskId = generateUuid();

  @Input() public set data(data: VisualizationData<SummaryData>) {
    if (!data.dataset.isInitial) this._data = data;
  }

  public get data(): VisualizationData<SummaryData> {
    return this._data;
  }

  public readonly component = input<ReportDashboardComponentViewData>(undefined!);
  public readonly fullscreenEnabled = input<boolean>(false);
  public readonly currColWidth = input<number>();
  public readonly resourcesPreviewAvailable = input<boolean>(undefined!);
  public readonly animated = input<boolean>(undefined!);
  public openResources = output<SummaryDataItem>();

  public itemsCount: number;
  public summaryShadowHidden: boolean;

  @ViewChild('singleContainerDiv', { read: ElementRef, static: false })
  public singleContainer: ElementRef;
  @ViewChild('multiContainerDiv', { read: ElementRef, static: false })
  public multiContainer: ElementRef;

  @ViewChild('stageCategoryValue', { read: TemplateRef })
  public stageCategoryValueTemplate: TemplateRef<SummaryData['items'][0]>;

  private _data: VisualizationData<SummaryData>;

  public ngAfterViewInit() {
    const size$ = this.resizeObserverEntry.resized.pipe(
      startWith({
        height: this.elRef.nativeElement.offsetHeight,
        width: this.elRef.nativeElement.offsetWidth,
      }),
      distinctUntilChanged(),
      observeOn(animationFrameScheduler),
      takeUntilDestroyed(this.destroyRef),
    );

    this.setShadow(false);

    const scroll$ = fromEvent(this.elRef.nativeElement, 'scroll').pipe(
      share(),
      takeUntilDestroyed(this.destroyRef),
    );
    const verticalEnd$ = scroll$.pipe(
      map((ev: any) => ev.target.scrollTop + ev.target.offsetHeight === ev.target.scrollHeight),
      distinctUntilChanged(),
    );

    verticalEnd$.subscribe(verticalEnd => {
      this.summaryShadowHidden = verticalEnd;
      this.setShadow(verticalEnd);
    });

    size$
      .pipe(take(1))
      .subscribe(() => this.renderer.addClass(this.elRef.nativeElement, 'size-determined'));

    size$.subscribe(({ width, height }) => {
      const container = this.multiContainer || this.singleContainer;

      const currColWidth = this.currColWidth();
      if (container && currColWidth) {
        const condition = this.multiContainer
          ? width <= currColWidth * 2
          : width > 250
            ? height < 90
            : true;

        if (condition) {
          this.renderer.addClass(container?.nativeElement, 'small');
        } else {
          this.renderer.removeClass(container?.nativeElement, 'small');
        }
      }
    });
  }

  public trackByFn(index: number, item: SummaryDataItem): number {
    return index;
  }

  public ngOnChanges(changes: SimpleChanges) {
    if ('data' in changes && this.data) {
      this.itemsCount = this.data.vis.items.length;

      requestAnimationFrame(() => {
        if (this.multiContainer) {
          this.setShadow(false);
        }
      });
    }
  }

  private setShadow(verticalEnd: boolean): void {
    const className = 'with-shadow';
    const scrollContainerElement = this.multiContainer?.nativeElement;

    if (!scrollContainerElement) return;

    if (verticalEnd) {
      this.summaryShadowHidden
        ? this.renderer.removeClass(scrollContainerElement, className)
        : this.renderer.addClass(scrollContainerElement, className);
    } else {
      this.elRef.nativeElement.scrollHeight <= this.elRef.nativeElement.offsetHeight
        ? this.renderer.removeClass(scrollContainerElement, className)
        : this.renderer.addClass(scrollContainerElement, className);
    }
  }
}
